import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { clientService } from '../services/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const ClientCreate = () => {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [loadingCep, setLoadingCep] = useState(false);

  // Estados separados para endereço
  const [addressFields, setAddressFields] = useState({
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: ''
  });

  const { register, handleSubmit, formState: { errors, isSubmitting }, reset, setValue } = useForm({
    defaultValues: {
      full_name: '',
      apelido: '',
      cpf: '',
      email: '',
      phone: '',
      document_id: '',
      birth_date: '',
      address: '',
    }
  });

  // Função para aplicar máscara de telefone
  const maskPhone = (value) => {
    if (!value) return '';
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    
    // Aplica máscara baseada no tamanho
    if (numbers.length <= 10) {
      // Telefone fixo: (00) 0000-0000
      return numbers
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    } else {
      // Celular: (00) 00000-0000
      return numbers
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d)/, '$1-$2')
        .replace(/(-\d{4})\d+?$/, '$1');
    }
  };

  // Função para aplicar máscara de CPF/CNPJ
  const maskDocument = (value) => {
    if (!value) return '';
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    
    // CPF: 11 dígitos
    if (numbers.length <= 11) {
      return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    } else {
      // CNPJ: 14 dígitos
      return numbers
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2')
        .replace(/(-\d{2})\d+?$/, '$1');
    }
  };

  const handlePhoneChange = (e) => {
    const maskedValue = maskPhone(e.target.value);
    setPhone(maskedValue);
    setValue('phone', maskedValue);
  };

  const handleDocumentChange = (e) => {
    const maskedValue = maskDocument(e.target.value);
    setDocumentId(maskedValue);
    setValue('document_id', maskedValue);
  };

  // Função para aplicar máscara de CEP
  const maskCep = (value) => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{5})(\d)/, '$1-$2').replace(/(-\d{3})\d+?$/, '$1');
  };

  // Função para buscar CEP via API ViaCEP
  const fetchCep = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) {
      toast.error('CEP deve conter 8 dígitos');
      return;
    }

    setLoadingCep(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/utils/cep/${cleanCep}`);
      const data = await response.json();

      if (data.erro) {
        toast.error('CEP não encontrado');
        return;
      }

      // Preencher campos automaticamente
      setAddressFields({
        ...addressFields,
        street: data.logradouro || '',
        neighborhood: data.bairro || '',
        city: data.localidade || '',
        state: data.uf || '',
        complement: data.complemento || ''
      });

      toast.success('CEP encontrado! Preencha o número e complemento se necessário.');
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      toast.error('Erro ao buscar CEP. Tente novamente.');
    } finally {
      setLoadingCep(false);
    }
  };

  const handleCepChange = (e) => {
    const maskedValue = maskCep(e.target.value);
    setAddressFields({ ...addressFields, cep: maskedValue });
  };

  const handleCepBlur = () => {
    if (addressFields.cep.replace(/\D/g, '').length === 8) {
      fetchCep(addressFields.cep);
    }
  };

  const handleAddressFieldChange = (field, value) => {
    setAddressFields({ ...addressFields, [field]: value });
  };

  // Função para construir endereço completo
  const buildFullAddress = () => {
    const parts = [];
    if (addressFields.street) parts.push(addressFields.street);
    if (addressFields.number) parts.push(`nº ${addressFields.number}`);
    if (addressFields.complement) parts.push(addressFields.complement);
    if (addressFields.neighborhood) parts.push(addressFields.neighborhood);
    if (addressFields.city) parts.push(addressFields.city);
    if (addressFields.state) parts.push(addressFields.state);
    if (addressFields.cep) parts.push(`CEP: ${addressFields.cep}`);
    
    return parts.join(', ');
  };

  const createMutation = useMutation({
    mutationFn: (data) => clientService.createClient(data),
    onSuccess: () => {
      toast.success('Cliente cadastrado com sucesso');
      reset();
      setPhone('');
      setDocumentId('');
      setAddressFields({
        cep: '',
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: ''
      });
      navigate('/dashboard');
    },
    onError: (error) => {
      const message = error?.response?.data?.message || 'Erro ao cadastrar cliente';
      toast.error(message);
    }
  });

  const onSubmit = (values) => {
    // Validar CPF/CNPJ opcional (se fornecido, deve ter 11 ou 14 dígitos)
    const cleanDoc = documentId.replace(/\D/g, '');
    if (cleanDoc && cleanDoc.length !== 11 && cleanDoc.length !== 14) {
      toast.error('CPF ou CNPJ inválido (deve ter 11 ou 14 dígitos)');
      return;
    }

    // Construir endereço completo
    const fullAddress = buildFullAddress();
    const submitData = {
      ...values,
      cpf: cleanDoc || null,
      address: fullAddress || values.address
    };
    createMutation.mutate(submitData);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-secondary-900">Cadastro de Cliente</h1>
        <p className="text-secondary-600">Preencha os dados básicos do cliente.</p>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-secondary-900">Dados Pessoais</h2>
          <p className="text-sm text-secondary-600">Informações obrigatórias e opcionais.</p>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Nome completo *</label>
                <input className="input" {...register('full_name', { required: 'Nome é obrigatório', minLength: { value: 3, message: 'Mínimo de 3 caracteres' } })} />
                {errors.full_name && <p className="text-sm text-danger-600 mt-1">{errors.full_name.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Apelido *</label>
                <input
                  className="input"
                  {...register('apelido', { required: 'Apelido é obrigatório', maxLength: { value: 100, message: 'Máximo de 100 caracteres' } })}
                  placeholder="Como o cliente será chamado"
                />
                {errors.apelido && <p className="text-sm text-danger-600 mt-1">{errors.apelido.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">CPF/CNPJ</label>
                <input
                  className="input"
                  value={documentId}
                  onChange={handleDocumentChange}
                  placeholder="000.000.000-00"
                  maxLength={14}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Email</label>
                <input className="input" type="email" {...register('email', { pattern: { value: /[^@\s]+@[^@\s]+\.[^@\s]+/, message: 'Email inválido' } })} />
                {errors.email && <p className="text-sm text-danger-600 mt-1">{errors.email.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Telefone</label>
                <input
                  className="input"
                  value={phone}
                  onChange={handlePhoneChange}
                  placeholder="(00) 00000-0000"
                  maxLength={15}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Data de Nascimento</label>
                <input className="input" type="date" {...register('birth_date')} />
              </div>

              {/* CEP */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">CEP *</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={addressFields.cep}
                    onChange={handleCepChange}
                    onBlur={handleCepBlur}
                    className="input flex-1"
                    placeholder="00000-000"
                    maxLength={9}
                  />
                  <button
                    type="button"
                    onClick={() => fetchCep(addressFields.cep)}
                    disabled={loadingCep || addressFields.cep.replace(/\D/g, '').length !== 8}
                    className="px-4 py-2 bg-[#49943d] text-white rounded-lg hover:bg-[#3a7630] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingCep ? '...' : 'Buscar'}
                  </button>
                </div>
              </div>

              {/* Rua */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Rua/Logradouro</label>
                <input
                  type="text"
                  value={addressFields.street}
                  onChange={(e) => handleAddressFieldChange('street', e.target.value)}
                  className="input"
                  placeholder="Nome da rua, avenida, etc."
                />
              </div>

              {/* Número e Complemento */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Número</label>
                <input
                  type="text"
                  value={addressFields.number}
                  onChange={(e) => handleAddressFieldChange('number', e.target.value)}
                  className="input"
                  placeholder="123"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Complemento</label>
                <input
                  type="text"
                  value={addressFields.complement}
                  onChange={(e) => handleAddressFieldChange('complement', e.target.value)}
                  className="input"
                  placeholder="Apto, Bloco, etc."
                />
              </div>

              {/* Bairro */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Bairro</label>
                <input
                  type="text"
                  value={addressFields.neighborhood}
                  onChange={(e) => handleAddressFieldChange('neighborhood', e.target.value)}
                  className="input"
                  placeholder="Nome do bairro"
                />
              </div>

              {/* Cidade e Estado */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Cidade</label>
                <input
                  type="text"
                  value={addressFields.city}
                  onChange={(e) => handleAddressFieldChange('city', e.target.value)}
                  className="input"
                  placeholder="Nome da cidade"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Estado (UF)</label>
                <select
                  value={addressFields.state}
                  onChange={(e) => handleAddressFieldChange('state', e.target.value)}
                  className="input"
                >
                  <option value="">Selecione</option>
                  <option value="AC">Acre</option>
                  <option value="AL">Alagoas</option>
                  <option value="AP">Amapá</option>
                  <option value="AM">Amazonas</option>
                  <option value="BA">Bahia</option>
                  <option value="CE">Ceará</option>
                  <option value="DF">Distrito Federal</option>
                  <option value="ES">Espírito Santo</option>
                  <option value="GO">Goiás</option>
                  <option value="MA">Maranhão</option>
                  <option value="MT">Mato Grosso</option>
                  <option value="MS">Mato Grosso do Sul</option>
                  <option value="MG">Minas Gerais</option>
                  <option value="PA">Pará</option>
                  <option value="PB">Paraíba</option>
                  <option value="PR">Paraná</option>
                  <option value="PE">Pernambuco</option>
                  <option value="PI">Piauí</option>
                  <option value="RJ">Rio de Janeiro</option>
                  <option value="RN">Rio Grande do Norte</option>
                  <option value="RS">Rio Grande do Sul</option>
                  <option value="RO">Rondônia</option>
                  <option value="RR">Roraima</option>
                  <option value="SC">Santa Catarina</option>
                  <option value="SP">São Paulo</option>
                  <option value="SE">Sergipe</option>
                  <option value="TO">Tocantins</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              <button type="button" className="btn-secondary" onClick={() => navigate('/dashboard')}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={isSubmitting || createMutation.isLoading}>
                {createMutation.isLoading ? 'Salvando...' : 'Salvar Cliente'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClientCreate; 